// src/pages/team/RosterAddPage.js
import React, { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { db, auth, functions } from "../../firebase";
import {
  collection,
  addDoc,
  doc,
  getDoc,
  getDocs,
  query,
  where,
  updateDoc,
  serverTimestamp,
} from "firebase/firestore";
import { v4 as uuidv4 } from "uuid";
import { httpsCallable } from "firebase/functions";
import { useAuthState } from "react-firebase-hooks/auth";

function RosterAddPage() {
  const { teamId } = useParams();
  const navigate = useNavigate();
  const [user] = useAuthState(auth);
  const [isLoading, setIsLoading] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [successMessage, setSuccessMessage] = useState("");
  const [teamName, setTeamName] = useState("");

  // Form fields with validation state
  const [formData, setFormData] = useState({
    firstName: { value: "", error: "" },
    lastName: { value: "", error: "" },
    email: { value: "", error: "" },
    birthday: { value: "", error: "" },
    gender: { value: "", error: "" },
    phone: { value: "", error: "" },
    relationship: { value: "", error: "" },
    isManager: false,
  });

  // Authorization check
  const [canManagePlayers, setCanManagePlayers] = useState(false);
  const [isPageLoading, setIsPageLoading] = useState(true);

  // Check if the current user is the manager and get team name
  useEffect(() => {
    if (!user) {
      setIsPageLoading(false);
      return;
    }
    
    const checkManagerStatus = async () => {
      try {
        const teamRef = doc(db, "teams", teamId);
        const teamSnap = await getDoc(teamRef);
        
        if (teamSnap.exists()) {
          // Get team name
          setTeamName(teamSnap.data().name || "Your Team");
          
          // Check if user is coach or admin
          const teamData = teamSnap.data();
          const isCoach = teamData.coachId === user.uid;
          const isAdmin = teamData.admins && 
                         Array.isArray(teamData.admins) && 
                         teamData.admins.includes(user.uid);
          
          if (isCoach || isAdmin) {
            setCanManagePlayers(true);
          } else {
            // Only coaches and admins can add players
            setCanManagePlayers(false);
          }
        } else {
          setErrorMessage("Team not found");
        }
      } catch (err) {
        console.error("Error checking team:", err);
        setErrorMessage("Error loading team information");
      } finally {
        setIsPageLoading(false);
      }
    };
    
    checkManagerStatus();
  }, [user, teamId]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === "checkbox") {
      setFormData({
        ...formData,
        [name]: checked,
      });
    } else {
      setFormData({
        ...formData,
        [name]: { ...formData[name], value },
      });
    }
  };

  // Validate form fields
  const validateForm = () => {
    let isValid = true;
    const newFormData = { ...formData };
    
    // Check required fields
    if (!formData.firstName.value.trim()) {
      newFormData.firstName.error = "First name is required";
      isValid = false;
    } else {
      newFormData.firstName.error = "";
    }
    
    if (!formData.lastName.value.trim()) {
      newFormData.lastName.error = "Last name is required";
      isValid = false;
    } else {
      newFormData.lastName.error = "";
    }
    
    // Email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!formData.email.value.trim()) {
      newFormData.email.error = "Email is required";
      isValid = false;
    } else if (!emailRegex.test(formData.email.value)) {
      newFormData.email.error = "Please enter a valid email";
      isValid = false;
    } else {
      newFormData.email.error = "";
    }
    
    // Phone validation (optional field)
    if (formData.phone.value.trim()) {
      const phoneRegex = /^\d{10}$|^\(\d{3}\)\s*\d{3}-\d{4}$|^\d{3}-\d{3}-\d{4}$/;
      if (!phoneRegex.test(formData.phone.value)) {
        newFormData.phone.error = "Please enter a valid phone number";
        isValid = false;
      } else {
        newFormData.phone.error = "";
      }
    }
    
    setFormData(newFormData);
    return isValid;
  };

  const handleAddPlayer = async (e) => {
    e.preventDefault();
    
    // Clear previous messages
    setErrorMessage("");
    setSuccessMessage("");
    
    // Validate form
    if (!validateForm()) {
      return;
    }
    
    if (!user) {
      setErrorMessage("You must be logged in to add a player.");
      return;
    }

    setIsLoading(true);

    try {
      // Extract values from formData
      const { 
        firstName: { value: firstName },
        lastName: { value: lastName },
        email: { value: email },
        birthday: { value: birthday },
        gender: { value: gender },
        phone: { value: phone },
        relationship: { value: relationship },
        isManager
      } = formData;

      // Step 1: Create invitation
      const token = uuidv4();
      await addDoc(collection(db, "invitations"), {
        teamId,
        teamName,
        invitedEmail: email,
        invitedName: `${firstName} ${lastName}`,
        invitedBy: user.uid,
        invitedByName: user.displayName || "Team Manager",
        token,
        createdAt: serverTimestamp(),
        expiresAt: new Date(Date.now() + 7 * 24 * 60 * 60 * 1000),
        used: false,
      });
        
      // Step 2: Send email invitation
      try {
        const sendInvitationEmail = httpsCallable(functions, "sendInvitationEmail");
        await sendInvitationEmail({ 
          invitedEmail: email, 
          invitedName: `${firstName} ${lastName}`,
          teamId, 
          teamName,
          token 
        });
      } catch (emailError) {
        console.warn("Email sending failed, but invitation created:", emailError);
        // Continue despite email error
      }

      // Step 3: Check if player already exists or create a new player document
      let playerId = null;
      try {
        // Search for a player with the same email
        const playersRef = collection(db, "players");
        const existingPlayerQuery = query(playersRef, where("email", "==", email));
        const existingPlayerSnap = await getDocs(existingPlayerQuery);
        
        if (existingPlayerSnap.empty) {
          // No player found with this email, create a new player document
          const playerData = {
            firstName,
            lastName,
            email,
            birthday: birthday || null,
            gender: gender || null,
            phone: phone || null,
            relationship: relationship || null,
            parentUID: user.uid,
            teamId,
            createdAt: serverTimestamp(),
            updatedAt: serverTimestamp(),
          };
          
          const newPlayerRef = await addDoc(playersRef, playerData);
          playerId = newPlayerRef.id;
          console.log("Created new player with ID:", playerId);
        } else {
          // Use existing player document
          const existingPlayer = existingPlayerSnap.docs[0];
          playerId = existingPlayer.id;
          console.log("Found existing player with ID:", playerId);
        }
      } catch (playerError) {
        console.error("Player creation/lookup failed:", playerError);
        // Continue despite player error
      }

      // Step 4: Try to find a user with this email (separate from player document)
      let userId = null;
      try {
        const usersRef = collection(db, "users");
        const userQuery = query(usersRef, where("email", "==", email));
        const userSnap = await getDocs(userQuery);
        
        if (!userSnap.empty) {
          // Found a user with this email
          userId = userSnap.docs[0].id;
          console.log("Found existing user with ID:", userId);
        } else {
          console.log("No user found with this email. Roster will be updated when user registers.");
        }
      } catch (userError) {
        console.error("User lookup failed:", userError);
      }

      // Step 5: Check for existing roster entry and create/update as needed
      try {
        // First check if player already has a roster entry for this team
        const rosterRef = collection(db, "rosters");
        const existingRosterQuery = query(
          rosterRef, 
          where("teamId", "==", teamId),
          where("playerEmail", "==", email)
        );
        const existingRosterSnap = await getDocs(existingRosterQuery);
        
        if (existingRosterSnap.empty) {
          // Create new roster entry only if one doesn't exist
          const rosterData = {
            teamId,
            playerName: `${firstName} ${lastName}`,
            playerEmail: email,
            status: "pending",
            createdAt: serverTimestamp(),
            startDate: new Date(),
            joinedAt: new Date(),
          };
          
          // Important: Add playerId from player document (not user ID)
          if (playerId) {
            rosterData.playerId = playerId;
            console.log("Using playerId in roster:", playerId);
          }
          
          // Add userId only if we found one (keep separate from playerId)
          if (userId) {
            rosterData.userId = userId;
            console.log("Using userId in roster:", userId);
          }
          
          await addDoc(collection(db, "rosters"), rosterData);
          console.log("Created new roster entry");
        } else {
          // Update existing roster entry instead of creating a new one
          const existingRosterId = existingRosterSnap.docs[0].id;
          const updateData = {
            status: "pending", // Reset to pending if re-invited
            updatedAt: serverTimestamp()
          };
          
          // Update playerId if we have one
          if (playerId) {
            updateData.playerId = playerId;
            console.log("Updating roster with playerId:", playerId);
          }
          
          // Update userId if we found one (keep separate from playerId)
          if (userId) {
            updateData.userId = userId;
            console.log("Updating roster with userId:", userId);
          }
          
          await updateDoc(doc(db, "rosters", existingRosterId), updateData);
          console.log("Updated existing roster entry");
        }
      } catch (rosterError) {
        console.error("Roster operation failed:", rosterError);
        // Continue anyway - invitation is sent
      }

      // Success!
      setSuccessMessage(`Invitation sent to ${firstName} ${lastName}!`);

      // Reset form
      setFormData({
        firstName: { value: "", error: "" },
        lastName: { value: "", error: "" },
        email: { value: "", error: "" },
        birthday: { value: "", error: "" },
        gender: { value: "", error: "" },
        phone: { value: "", error: "" },
        relationship: { value: "", error: "" },
        isManager: false,
      });

      // Delay navigation to show success message
      setTimeout(() => {
        navigate(`/team/${teamId}/dashboard/roster`);
      }, 2000);
      
    } catch (err) {
      console.error("Error in add player process:", err);
      setErrorMessage("There was an issue sending the invitation. Please try again.");
    } finally {
      setIsLoading(false);
    }
  };

  if (isPageLoading) {
    return (
      <div className="loading-container" style={{ 
        display: "flex", 
        justifyContent: "center", 
        alignItems: "center", 
        height: "200px" 
      }}>
        <div className="spinner" style={{ 
          width: "40px", 
          height: "40px", 
          border: "4px solid rgba(0, 119, 182, 0.1)", 
          borderRadius: "50%", 
          borderTop: "4px solid #0077B6", 
          animation: "spin 1s linear infinite" 
        }}></div>
        <style>
          {`@keyframes spin { 0% { transform: rotate(0deg); } 100% { transform: rotate(360deg); } }`}
        </style>
      </div>
    );
  }

  if (!canManagePlayers) {
    return (
      <div style={{
        padding: "20px",
        backgroundColor: "#F8F9FA",
        borderRadius: "10px",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
        textAlign: "center"
      }}>
        <h3 style={{ color: "#0077B6" }}>Access Denied</h3>
        <p>You are not authorized to add players. Only the team manager can do this.</p>
        <button
          onClick={() => navigate(`/team/${teamId}/dashboard`)}
          style={{
            background: "#0077B6",
            color: "white",
            border: "none",
            borderRadius: "5px",
            padding: "10px",
            cursor: "pointer",
            marginTop: "15px"
          }}
        >
          Back to Dashboard
        </button>
      </div>
    );
  }

  return (
    <div
      style={{
        padding: "20px",
        backgroundColor: "#F8F9FA",
        borderRadius: "10px",
        boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
        maxWidth: "600px",
        margin: "0 auto"
      }}
    >
      <h3 style={{ color: "#0077B6", marginBottom: "20px" }}>
        Add a New Player to {teamName}
      </h3>
      
      {/* Error Message */}
      {errorMessage && (
        <div style={{
          backgroundColor: "#FFECEC",
          color: "#721C24",
          padding: "10px 15px",
          borderRadius: "5px",
          marginBottom: "15px",
          border: "1px solid #F5C6CB"
        }}>
          {errorMessage}
        </div>
      )}
      
      {/* Success Message */}
      {successMessage && (
        <div style={{
          backgroundColor: "#D4EDDA",
          color: "#155724",
          padding: "10px 15px",
          borderRadius: "5px",
          marginBottom: "15px",
          border: "1px solid #C3E6CB"
        }}>
          {successMessage}
        </div>
      )}
      
      <form onSubmit={handleAddPlayer}>
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
            First Name <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="firstName"
            value={formData.firstName.value}
            onChange={handleInputChange}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: formData.firstName.error ? "1px solid #dc3545" : "1px solid #ccc",
              outline: "none",
              transition: "border 0.2s",
            }}
            placeholder="Enter first name"
          />
          {formData.firstName.error && (
            <p style={{ color: "#dc3545", fontSize: "14px", margin: "5px 0 0" }}>
              {formData.firstName.error}
            </p>
          )}
        </div>
        
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
            Last Name <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="text"
            name="lastName"
            value={formData.lastName.value}
            onChange={handleInputChange}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: formData.lastName.error ? "1px solid #dc3545" : "1px solid #ccc",
              outline: "none",
            }}
            placeholder="Enter last name"
          />
          {formData.lastName.error && (
            <p style={{ color: "#dc3545", fontSize: "14px", margin: "5px 0 0" }}>
              {formData.lastName.error}
            </p>
          )}
        </div>
        
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
            Email <span style={{ color: "red" }}>*</span>
          </label>
          <input
            type="email"
            name="email"
            value={formData.email.value}
            onChange={handleInputChange}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: formData.email.error ? "1px solid #dc3545" : "1px solid #ccc",
              outline: "none",
            }}
            placeholder="Enter email address"
          />
          {formData.email.error && (
            <p style={{ color: "#dc3545", fontSize: "14px", margin: "5px 0 0" }}>
              {formData.email.error}
            </p>
          )}
        </div>
        
        <div style={{ display: "flex", gap: "15px", marginBottom: "15px" }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
              Birthday
            </label>
            <input
              type="date"
              name="birthday"
              value={formData.birthday.value}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                outline: "none",
              }}
            />
          </div>
          
          <div style={{ flex: 1 }}>
            <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
              Gender
            </label>
            <select
              name="gender"
              value={formData.gender.value}
              onChange={handleInputChange}
              style={{
                width: "100%",
                padding: "10px",
                borderRadius: "5px",
                border: "1px solid #ccc",
                outline: "none",
                appearance: "none",
                backgroundImage: "url('data:image/svg+xml;utf8,<svg fill=\"black\" height=\"24\" viewBox=\"0 0 24 24\" width=\"24\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M7 10l5 5 5-5z\"/></svg>')",
                backgroundRepeat: "no-repeat",
                backgroundPosition: "right 10px center",
                backgroundColor: "white"
              }}
            >
              <option value="">Select gender</option>
              <option value="Male">Male</option>
              <option value="Female">Female</option>
              <option value="Other">Other</option>
              <option value="Prefer not to say">Prefer not to say</option>
            </select>
          </div>
        </div>
        
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
            Phone
          </label>
          <input
            type="tel"
            name="phone"
            value={formData.phone.value}
            onChange={handleInputChange}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: formData.phone.error ? "1px solid #dc3545" : "1px solid #ccc",
              outline: "none",
            }}
            placeholder="(123) 456-7890"
          />
          {formData.phone.error && (
            <p style={{ color: "#dc3545", fontSize: "14px", margin: "5px 0 0" }}>
              {formData.phone.error}
            </p>
          )}
        </div>
        
        <div style={{ marginBottom: "15px" }}>
          <label style={{ display: "block", marginBottom: "5px", fontWeight: "500" }}>
            Relationship
          </label>
          <select
            name="relationship"
            value={formData.relationship.value}
            onChange={handleInputChange}
            style={{
              width: "100%",
              padding: "10px",
              borderRadius: "5px",
              border: "1px solid #ccc",
              outline: "none",
              appearance: "none",
              backgroundImage: "url('data:image/svg+xml;utf8,<svg fill=\"black\" height=\"24\" viewBox=\"0 0 24 24\" width=\"24\" xmlns=\"http://www.w3.org/2000/svg\"><path d=\"M7 10l5 5 5-5z\"/></svg>')",
              backgroundRepeat: "no-repeat",
              backgroundPosition: "right 10px center",
              backgroundColor: "white"
            }}
          >
            <option value="">Select relationship</option>
            <option value="Parent">Parent</option>
            <option value="Mom">Mom</option>
            <option value="Dad">Dad</option>
            <option value="Guardian">Guardian</option>
            <option value="Coach">Coach</option>
            <option value="Assistant Coach">Assistant Coach</option>
            <option value="Player">Player</option>
            <option value="Other">Other</option>
          </select>
        </div>
        
        <div style={{ marginBottom: "20px" }}>
          <label style={{ 
            display: "flex", 
            alignItems: "center", 
            cursor: "pointer",
            padding: "10px",
            borderRadius: "5px",
            backgroundColor: formData.isManager ? "rgba(0, 119, 182, 0.1)" : "transparent"
          }}>
            <input
              type="checkbox"
              name="isManager"
              checked={formData.isManager}
              onChange={handleInputChange}
              style={{ marginRight: "10px" }}
            />
            <div>
              <span style={{ fontWeight: "500" }}>Make this person a team manager</span>
              <p style={{ margin: "5px 0 0", fontSize: "14px", color: "#6c757d" }}>
                Team managers can add/remove players and manage team settings
              </p>
            </div>
          </label>
        </div>
        
        <div style={{ display: "flex", gap: "10px", justifyContent: "space-between" }}>
          <button
            type="button"
            onClick={() => navigate(`/team/${teamId}/dashboard/roster`)}
            style={{
              background: "white",
              color: "#0077B6",
              border: "1px solid #0077B6",
              borderRadius: "5px",
              padding: "10px 15px",
              cursor: "pointer",
              flexBasis: "48%",
              transition: "background 0.3s",
              fontSize: "16px",
            }}
          >
            Cancel
          </button>
          
          <button
            type="submit"
            disabled={isLoading}
            style={{
              background: "#0077B6",
              color: "white",
              border: "none",
              borderRadius: "5px",
              padding: "10px 15px",
              cursor: isLoading ? "not-allowed" : "pointer",
              flexBasis: "48%",
              transition: "background 0.3s",
              opacity: isLoading ? 0.7 : 1,
              display: "flex",
              justifyContent: "center",
              alignItems: "center",
              fontSize: "16px",
            }}
          >
            {isLoading ? (
              <>
                <span style={{ 
                  display: "inline-block", 
                  width: "16px", 
                  height: "16px", 
                  border: "2px solid rgba(255,255,255,0.3)", 
                  borderRadius: "50%", 
                  borderTopColor: "white", 
                  animation: "spin 1s linear infinite",
                  marginRight: "8px"
                }}></span>
                Sending...
              </>
            ) : (
              "Add Player & Send Invitation"
            )}
          </button>
        </div>
      </form>
    </div>
  );
}

export default RosterAddPage;